
/**
 * Module dependencies.
 */

import {
  Action,
  FilterType,
  Filters,
  FiltersListContext,
  Value
} from './context';

import { useRouter } from 'next/router';
import React, { ReactNode, useCallback, useMemo, useReducer } from 'react';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import omit from 'lodash/omit';
import qs from 'qs';
import split from 'lodash/split';

/**
 * `Props` type.
 */

type Props = {
  children: ReactNode,
  omitUrl?: boolean
};

/**
 * `Payload` type.
 */

type Payload = {
  filterType: FilterType,
  value: Value
};

/**
 * `State` type.
 */

type State = {
  filters: Filters,
  submittedFilters: Filters
};

/**
 * Action types.
 */

const actionTypes = {
  addFilter: 'ADD_FILTER',
  removeFilter: 'REMOVE_FILTER',
  resetFilters: 'RESET_FILTERS',
  submitClearFilters: 'SUBMIT_CLEAR_FILTERS',
  submitFilters: 'SUBMIT_FILTERS',
  submitRemoveFilters: 'SUBMIT_REMOVE_FILTERS'
};

/**
 * Normalize filter on add.
 */

function normalizeFilterOnAdd(filters: Filters, payload: Payload): any {
  const filtersState = filters ?? {};
  const { filterType, value } = payload;
  const hasFilterType = Object.keys(filtersState).includes(filterType);

  return {
    ...filtersState,
    [filterType]: hasFilterType ? `${filtersState[filterType]},${value}` : value
  };
}

/**
 * Normalize filter on remove.
 */

function normalizeFilterOnRemove(filters: Filters, payload: Payload) {
  const filtersState = filters ?? {};
  const { filterType, value } = payload;
  const newValue = filtersState[filterType]
    // @ts-ignore
    .split(',')
    .filter((key: string) => key !== value)
    .join(',');

  if (isEmpty(newValue)) {
    return omit(filtersState, [filterType]);
  }

  return {
    ...filtersState,
    [filterType]: newValue
  };
}

/**
 * Reducer.
 */

function reducer(state: State, { payload, type }) {
  switch (type) {
    case actionTypes.addFilter: {
      return {
        ...state,
        filters: normalizeFilterOnAdd(state?.filters, payload)
      };
    }

    case actionTypes.removeFilter: {
      return {
        ...state,
        filters: normalizeFilterOnRemove(state?.filters, payload)
      };
    }

    case actionTypes.resetFilters: {
      return {
        ...state,
        filters: state?.submittedFilters
      };
    }

    case actionTypes.submitClearFilters: {
      return {
        filters: {},
        submittedFilters: {}
      };
    }

    case actionTypes.submitFilters: {
      return {
        ...state,
        submittedFilters: state?.filters
      };
    }

    case actionTypes.submitRemoveFilters: {
      return {
        filters: normalizeFilterOnRemove(state?.filters, payload),
        submittedFilters: normalizeFilterOnRemove(state?.submittedFilters, payload)
      };
    }

    default:
      return state;
  }
}

/**
 * `useInitialState` hook.
 */

function useInitialState(omitUrl: boolean): State {
  const router = useRouter();
  const rawQuery = get(router, 'query');
  let query = null;

  if (!omitUrl) {
    if (rawQuery && typeof rawQuery === 'object') {
      query = qs.parse(qs.stringify(rawQuery));
    } else {
      query = qs.parse(rawQuery || '');
    }
  }

  return {
    filters: query?.filter ?? null,
    submittedFilters: query?.filter ?? null
  };
}

/**
 * `FiltersListProvider` provider.
 */

const FiltersListProvider = ({ children, omitUrl }: Props) => {
  const initialState = useInitialState(omitUrl);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { filters, submittedFilters } = state;

  // On add value from filter.
  const handleAdd = useCallback((filterType: FilterType, value: Value) => {
    const dispatchedAction = {
      payload: { filterType, value },
      type: actionTypes.addFilter
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // On remove value from filter.
  const handleRemove = useCallback((filterType: FilterType, value: Value) => {
    const dispatchedAction = {
      payload: { filterType, value },
      type: actionTypes.removeFilter
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // On add/remove filter dependes on action ('add' | 'remove').
  const handleAddRemove = useCallback((filterType: FilterType, value: Value, action: Action) => {
    if (action === 'add') {
      return handleAdd(filterType, value);
    }

    return handleRemove(filterType, value);
  }, [handleAdd, handleRemove]);

  // On submit filters.
  const handleSubmit = useCallback(() => {
    const dispatchedAction = {
      payload: null,
      type: actionTypes.submitFilters
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // On submit clear filters.
  const handleSubmitClear = useCallback(() => {
    const dispatchedAction = {
      payload: null,
      type: actionTypes.submitClearFilters
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // On submit remove value from filter.
  const handleSubmitRemove = useCallback((filterType: FilterType, value: Value) => {
    const dispatchedAction = {
      payload: { filterType, value },
      type: actionTypes.submitRemoveFilters
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // On reset filters.
  const handleReset = useCallback(() => {
    const dispatchedAction = {
      payload: null,
      type: actionTypes.resetFilters
    };

    dispatch(dispatchedAction);

    return reducer(state, dispatchedAction);
  }, [state]);

  // Check if value is selected.
  const handleIsSelected = useCallback((filterType: FilterType, value: Value) => {
    const filtersState = filters ?? {};
    const hasFilterType = Object.keys(filtersState).includes(filterType);
    const hasValue = hasFilterType && split(filtersState[filterType], ',').includes(value);

    return hasValue;
  }, [filters]);

  const normalizedSelectedFilters = useMemo(() => {
    return isEmpty(filters) ? null : filters;
  }, [filters]);

  const normalizedSubmittedFilters = useMemo(() => {
    return isEmpty(submittedFilters) ? null : submittedFilters;
  }, [submittedFilters]);

  return (
    <FiltersListContext.Provider
      value={{
        filters: normalizedSelectedFilters,
        isFilterSelected: handleIsSelected,
        omitUrl,
        onAdd: handleAdd,
        onAddRemove: handleAddRemove,
        onSubmit: handleSubmit,
        onSubmitClear: handleSubmitClear,
        onSubmitRemove: handleSubmitRemove,
        reset: handleReset,
        submittedFilters: normalizedSubmittedFilters
      }}
    >
      {children}
    </FiltersListContext.Provider>
  );
};

/**
 * Export `FiltersListProvider` provider.
 */

export default FiltersListProvider;
